/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppTheme } from "../../../app/providers/ThemeProvider";
import { INITIAL_REGION, type OsmTileStyle } from "../constants";
import {
  filterMapPois,
  getMapPois,
  recordPoiAccess,
} from "../services/mapService";
import type { MapPoi, MapPoiCategory } from "../services/map.types";
import {
  checkLocationPermission,
  getCurrentCoordinates,
  requestLocationPermission,
} from "../utils/geolocation";
import type { MapLeafletRef } from "../components/MapLeaflet";

type MapCoordinate = { latitude: number; longitude: number };

type UseMapOverviewOptions = {
  focusPoiId?: string;
};

export function useMapOverview(options: UseMapOverviewOptions = {}) {
  const { isDarkMode } = useAppTheme();
  const mapRef = useRef<MapLeafletRef>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [isSearchOverlayVisible, setIsSearchOverlayVisible] = useState(false);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState<MapPoiCategory | null>(
    null
  );
  const [isNearMeActive, setIsNearMeActive] = useState(false);
  const [isNearMeResolving, setIsNearMeResolving] = useState(false);
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [selectedPoi, setSelectedPoi] = useState<MapPoi | null>(null);
  const [userCoords, setUserCoords] = useState<MapCoordinate | null>(null);
  const [tileStyle, setTileStyle] = useState<OsmTileStyle>(
    isDarkMode ? "dark" : "standard"
  );
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);

  useEffect(() => {
    setTileStyle(isDarkMode ? "dark" : "standard");
  }, [isDarkMode]);

  const pois = useMemo(() => filterMapPois(search), [search]);

  const resetMapRegion = useCallback(() => {
    mapRef.current?.animateToCoordinate(
      INITIAL_REGION.latitude,
      INITIAL_REGION.longitude,
      INITIAL_REGION.latitudeDelta
    );
  }, []);

  const focusOnCoordinates = useCallback(
    (coords: MapCoordinate, delta = 0.08) => {
      mapRef.current?.animateToCoordinate(
        coords.latitude,
        coords.longitude,
        delta
      );
    },
    []
  );

  const focusOnPoiById = useCallback(
    (poiId: string) => {
      const poi = getMapPois().find((item) => item.id === poiId);
      if (!poi) return;
      setSelectedPoi(poi);
      setIsSearchOverlayVisible(false);
      setIsLegendVisible(false);
      focusOnCoordinates(
        { latitude: poi.latitude, longitude: poi.longitude },
        0.05
      );
      recordPoiAccess(poiId);
    },
    [focusOnCoordinates]
  );

  const applySearch = useCallback((value: string) => {
    setSearch(value);
    setSelectedPoi(null);
    setIsSearchOverlayVisible(false);
  }, []);

  const openSearchOverlay = useCallback(() => {
    setIsSearchOverlayVisible(true);
    setIsLegendVisible(false);
  }, []);

  const closeSearchOverlay = useCallback(() => {
    setIsSearchOverlayVisible(false);
  }, []);

  const toggleLegend = useCallback(() => {
    setIsLegendVisible((current) => !current);
    setIsSearchOverlayVisible(false);
  }, []);

  const selectCategory = useCallback(
    (category: MapPoiCategory) => {
      setIsNearMeActive(false);
      setActiveCategory((current) => {
        const next = current === category ? null : category;
        setIsCategoryModalVisible(next !== null);
        setSelectedPoi(null);
        if (next === null) {
          resetMapRegion();
        }
        return next;
      });
    },
    [resetMapRegion]
  );

  const resolveNearMeLocation = useCallback(async (): Promise<boolean> => {
    setIsNearMeResolving(true);
    const cachedCoords = userCoords;
    if (cachedCoords) {
      focusOnCoordinates(cachedCoords, 0.12);
    }

    const coords =
      (await getCurrentCoordinates({ timeoutMs: 12_000 })) ?? cachedCoords;
    if (!coords) {
      setIsNearMeActive(false);
      setIsNearMeResolving(false);
      setIsCategoryModalVisible(false);
      setIsLocationModalVisible(true);
      return false;
    }

    setUserCoords(coords);
    setIsNearMeActive(true);
    setIsCategoryModalVisible(true);
    focusOnCoordinates(coords, 0.12);
    setIsNearMeResolving(false);
    return true;
  }, [focusOnCoordinates, userCoords]);

  const selectNearMe = useCallback(async () => {
    setActiveCategory(null);
    setSelectedPoi(null);

    if (isNearMeActive) {
      setIsNearMeActive(false);
      setIsNearMeResolving(false);
      setIsCategoryModalVisible(false);
      resetMapRegion();
      return;
    }

    setIsNearMeActive(true);
    setIsNearMeResolving(true);
    setIsCategoryModalVisible(true);

    const status = await requestLocationPermission();
    if (status === "blocked" || status !== "granted") {
      setIsNearMeActive(false);
      setIsNearMeResolving(false);
      setIsCategoryModalVisible(false);
      setIsLocationModalVisible(true);
      return;
    }

    await resolveNearMeLocation();
  }, [isNearMeActive, resetMapRegion, resolveNearMeLocation]);

  const closeCategoryModal = useCallback(() => {
    setIsCategoryModalVisible(false);
    if (isNearMeActive) {
      setIsNearMeActive(false);
      setIsNearMeResolving(false);
      resetMapRegion();
    }
  }, [isNearMeActive, resetMapRegion]);

  const refreshUserLocation = useCallback(async () => {
    const coords = await getCurrentCoordinates();
    if (coords) {
      setUserCoords(coords);
      focusOnCoordinates(coords, 0.05);
    }
  }, [focusOnCoordinates]);

  const focusOnUserLocation = useCallback(async () => {
    const status = await requestLocationPermission();
    if (status === "blocked" || status !== "granted") {
      setIsLocationModalVisible(true);
      return;
    }
    await refreshUserLocation();
  }, [refreshUserLocation]);

  useEffect(() => {
    (async () => {
      const status = await checkLocationPermission();
      if (status === "granted") {
        const coords = await getCurrentCoordinates();
        if (coords) setUserCoords(coords);
      }
    })();
  }, []);

  useEffect(() => {
    if (!options.focusPoiId) return;
    focusOnPoiById(options.focusPoiId);
  }, [options.focusPoiId, focusOnPoiById]);

  useEffect(() => {
    const poiId = searchParams.get("poiId");
    if (poiId) {
      focusOnPoiById(poiId);
      searchParams.delete("poiId");
      setSearchParams(searchParams, { replace: true });
    }
  }, [focusOnPoiById, searchParams, setSearchParams]);

  const toggleMapStyle = useCallback(() => {
    setTileStyle((current) => (current === "standard" ? "dark" : "standard"));
  }, []);

  return {
    mapRef,
    search,
    setSearch,
    isSearchOverlayVisible,
    openSearchOverlay,
    closeSearchOverlay,
    applySearch,
    isLegendVisible,
    toggleLegend,
    activeCategory,
    isNearMeActive,
    isNearMeResolving,
    selectCategory,
    selectNearMe,
    resolveNearMeLocation,
    isCategoryModalVisible,
    closeCategoryModal,
    selectedPoi,
    setSelectedPoi,
    userCoords,
    tileStyle,
    toggleMapStyle,
    pois,
    isLocationModalVisible,
    setIsLocationModalVisible,
    focusOnUserLocation,
    focusOnPoiById,
    initialRegion: INITIAL_REGION,
  };
}
